import React from "react";
import { motion } from "framer-motion";
import { Smile, Brain, Activity, Briefcase, TrendingUp } from "lucide-react";
import useWellnessScores from "hooks/useWellnessScores";
import useWellnessTasks from "hooks/useWellnessTasks";
import WellnessScoreCard from "components/wellness-score-card/wellness-score-card";

// Category configuration
const categories = [
  {
    key: "emotionalHealth",
    historyKey: "emotionalHealthScore",
    title: "Emotional Health",
    icon: Smile,
    color: "#f97316",
    colorClass: "from-orange-500 to-orange-600",
  },
  {
    key: "stressAndCoping",
    historyKey: "stressAndCopingScore",
    title: "Stress & Coping",
    icon: Brain,
    color: "#22c55e",
    colorClass: "from-green-500 to-green-600",
  },
  {
    key: "physicalHealthFitness",
    historyKey: "physicalHealthFitnessScore",
    title: "Physical Health",
    icon: Activity,
    color: "#14b8a6",
    colorClass: "from-teal-500 to-teal-600",
  },
  {
    key: "academicCareer",
    historyKey: "academicCareerScore",
    title: "Career Growth",
    icon: Briefcase,
    color: "#a855f7",
    colorClass: "from-purple-500 to-purple-600",
  },
];

// Loading skeleton component
const LoadingSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-8 w-64 bg-gray-700 rounded mb-4 mx-auto"></div>
    <div className="grid md:grid-cols-2 gap-6">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gray-700"></div>
            <div className="h-5 w-32 bg-gray-700 rounded"></div>
          </div>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-full bg-gray-700"></div>
            <div className="flex-1 h-12 bg-gray-700 rounded"></div>
          </div>
          <div className="h-4 w-full bg-gray-700 rounded mb-2"></div>
          <div className="h-4 w-3/4 bg-gray-700 rounded mb-4"></div>
          <div className="bg-gray-900/50 rounded-xl p-4">
            <div className="h-4 w-24 bg-gray-700 rounded mb-2"></div>
            <div className="h-4 w-full bg-gray-700 rounded mb-1"></div>
            <div className="h-4 w-5/6 bg-gray-700 rounded"></div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const WellnessScoresDashboard = () => {
  const { data, loading, error } = useWellnessScores();
  const { data: tasksData, completeTask } = useWellnessTasks();

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12">
        <LoadingSkeleton />
      </div>
    );
  }

  if (error || !data?.current) {
    // Don't show anything if there's an error or no data
    return null;
  }

  const { current, history } = data;

  // Extract trend data for each category from history
  const getTrendData = (historyKey) => {
    return history.map((entry) => entry[historyKey] || 0);
  };

  const overallScore = Math.round(current.totalScore * 10) / 10; // Keep one decimal place
  const scorePercentage = current.totalScore / 10; // 0-10 scale to 0-1
  const circumference = 2 * Math.PI * 54; // radius of 54
  const strokeDashoffset = circumference * (1 - scorePercentage);

  // Color based on score (0-10 scale)
  const getScoreColor = (score) => {
    if (score >= 7) return "#22c55e";
    if (score >= 4) return "#f59e0b";
    return "#ef4444";
  };

  const strokeColor = getScoreColor(overallScore);

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      {/* Header and Overall Score Combined */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-10"
      >
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
          {/* Title Section */}
          <div className="text-center lg:text-left">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
              Wellness Dashboard
            </h2>
            <p className="text-gray-400 text-lg">
              Track your progress across different categories
            </p>
          </div>

          {/* Overall Score - Circular Progress */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 100 }}
            className="relative bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50"
          >
            <div className="flex items-center gap-6">
              {/* Circular Progress Ring */}
              <div className="relative w-32 h-32">
                <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 120 120">
                  {/* Background circle */}
                  <circle
                    cx="60"
                    cy="60"
                    r="54"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    className="text-gray-700/50"
                  />
                  {/* Progress circle */}
                  <motion.circle
                    cx="60"
                    cy="60"
                    r="54"
                    stroke={strokeColor}
                    strokeWidth="8"
                    fill="none"
                    strokeLinecap="round"
                    initial={{ strokeDashoffset: circumference }}
                    animate={{ strokeDashoffset }}
                    transition={{ duration: 1.5, ease: "easeOut", delay: 0.3 }}
                    style={{ strokeDasharray: circumference }}
                  />
                </svg>
                {/* Score in center */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="text-4xl font-bold text-white"
                  >
                    {overallScore}
                  </motion.span>
                  <span className="text-xs text-gray-400 uppercase tracking-wider">
                    of 10
                  </span>
                </div>
              </div>

              {/* Score Details */}
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-1">
                  Overall Score
                </p>
                <p className="text-2xl font-semibold text-white mb-2">
                  {overallScore >= 7 ? "Good" : overallScore >= 4 ? "Fair" : "Needs Work"}
                </p>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <TrendingUp size={16} className="text-blue-400" />
                  <span>Based on all categories</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Category Cards Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {categories.map((category, index) => {
          const categoryData = current[category.key];
          const trendData = getTrendData(category.historyKey);
          const categoryTasks = tasksData?.[category.key] || [];

          return (
            <motion.div
              key={category.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + index * 0.1 }}
            >
              <WellnessScoreCard
                title={category.title}
                icon={category.icon}
                color={category.color}
                colorClass={category.colorClass}
                score={categoryData?.score || 0}
                trend={trendData}
                summary={categoryData?.summary}
                improvementPlan={categoryData?.improvementPlan}
                tasks={categoryTasks}
                onCompleteTask={completeTask}
              />
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default WellnessScoresDashboard;
